import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { authFetch, handleAuthError } from "@/utils/authErrors";
import PasswordStrengthSideIndicator from "./PasswordStrengthSideIndicator";
import { AgreementCheckbox } from "@/components/consent";
import { useLatestAgreements } from "@/hooks/useLatestAgreements";
import { useRegistrationFlow } from "@/hooks/useRegistrationFlow";
import RegistrationBlockingModal from "./RegistrationBlockingModal";

/**
 * DomesticRegisterPanel 组件属性
 */
export interface DomesticRegisterPanelProps {
  /** 邀请 token（可选） */
  inviteToken?: string | null;
  /** 注册成功后重定向路径 */
  redirectTo?: string;
}

/**
 * 国内注册面板
 * 支持：手机号+验证码注册
 * 支持邀请链接注册（invite_token）
 */
export default function DomesticRegisterPanel({
  inviteToken,
  redirectTo = "/",
}: DomesticRegisterPanelProps) {
  const { t } = useTranslation(["login", "consent"]);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [password, setPassword] = useState("");
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [agreementsChecked, setAgreementsChecked] = useState(false);
  const [agreementError, setAgreementError] = useState<string | undefined>();

  const phoneRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // 获取最新协议
  const { agreements } = useLatestAgreements({ regionCode: "cn" });

  // 区域感知的注册流程逻辑
  const { handleSendVerificationCode, modalProps } = useRegistrationFlow({
    agreementsChecked,
    setAgreementsChecked,
    agreements,
  });

  // 倒计时
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // 发送验证码的实际逻辑
  const doSendCode = async () => {
    const phone = phoneRef.current?.value.trim();
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      toast.error(t("common.invalidPhone"));
      return;
    }

    try {
      const resp = await authFetch("/users/send_verification_code", {
        method: "POST",
        body: JSON.stringify({ phone }),
      });
      const data = await resp.json();
      if (data.success) {
        toast.success(t("domestic.codeSent"));
        setCountdown(60);
      } else {
        handleAuthError(
          data.error,
          t as (key: string) => string,
          "common.registerFailed",
        );
      }
    } catch {
      toast.error(t("common.networkError"));
    }
  };

  // 发送验证码（带协议阻断检查 - CN 区域在此触发）
  const sendCode = () => {
    const phone = phoneRef.current?.value.trim();
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      toast.error(t("common.invalidPhone"));
      return;
    }

    // 使用区域感知的注册流程逻辑
    // CN 区域在发送验证码时触发协议确认
    handleSendVerificationCode(doSendCode);
  };

  // 表单提交
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const phone = phoneRef.current?.value.trim();
    const code = codeRef.current?.value.trim();

    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      toast.error(t("common.invalidPhone"));
      return;
    }

    if (!code || code.length !== 6) {
      toast.error(t("common.invalidCode"));
      return;
    }

    if (!password || password.length < 6) {
      toast.error(t("common.invalidPassword"));
      return;
    }

    // 检查协议是否已勾选
    if (!agreementsChecked) {
      setAgreementError(
        t("consent:error.agreementRequired", "请阅读并同意服务条款和隐私政策"),
      );
      return;
    }
    setAgreementError(undefined);

    setIsLoading(true);

    try {
      const formData = new FormData();
      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content || "";

      formData.append("authenticity_token", csrfToken);
      formData.append("user[phone]", phone);
      formData.append("user[verification_code]", code);
      formData.append("user[password]", password);
      formData.append("user[country_region]", "cn");
      formData.append("user[signup_channel]", "phone");

      if (inviteToken) {
        formData.append("user[invite_token]", inviteToken);
      }

      // 添加协议签署信息
      const agreementIds = agreements
        .filter((a) => a.type === "tos" || a.type === "privacy")
        .map((a) => a.id);
      if (agreementIds.length > 0) {
        formData.append("user[agreement_ids]", agreementIds.join(","));
      }

      const response = await fetch("/users", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "text/html",
          "Accept-Language": localStorage.getItem("i18nextLng") || "zh",
        },
        credentials: "same-origin",
      });

      if (response.ok) {
        toast.success(t("common.registerSuccess", "注册成功"));
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 500);
        // Success: Keep loading true
      } else {
        setIsLoading(false);
        handleAuthError(
          { code: "REGISTER_FAILED" },
          t as (key: string) => string,
          "common.registerFailed",
        );
      }
    } catch {
      setIsLoading(false);
      toast.error(t("common.networkError"));
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label={t("domestic.loginRegisterButton")}
      aria-busy={isLoading}
    >
      <div className="flex gap-2">
        <Input
          id="reg-phone"
          type="tel"
          placeholder={t("domestic.phonePlaceholder")}
          ref={phoneRef}
          autoComplete="tel"
          aria-required="true"
          aria-label={t("domestic.phoneLabel")}
          className="flex-1 bg-background dark:bg-background h-10"
        />
        <Button
          type="button"
          variant="outline"
          disabled={countdown > 0 || isLoading}
          onClick={sendCode}
          className="shrink-0 h-10"
          aria-label={
            countdown > 0
              ? String((t as any)("domestic.resendIn", { seconds: countdown }))
              : String(t("domestic.sendCode"))
          }
        >
          {countdown > 0 ? `${countdown}s` : t("domestic.sendCode")}
        </Button>
      </div>

      <div>
        <Input
          id="reg-code"
          type="text"
          inputMode="numeric"
          pattern="\d{6}"
          placeholder={t("domestic.codePlaceholder")}
          ref={codeRef}
          autoComplete="one-time-code"
          aria-required="true"
          aria-label={t("domestic.codeLabel")}
          className="bg-background dark:bg-background h-10"
        />
      </div>

      <div className="relative">
        <Input
          id="reg-password"
          type="password"
          placeholder={t("domestic.passwordPlaceholder")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => setPasswordFocused(false)}
          autoComplete="new-password"
          aria-required="true"
          aria-label={t("domestic.passwordLabel")}
          className="bg-background dark:bg-background h-10"
        />
        <PasswordStrengthSideIndicator
          password={password}
          visible={passwordFocused}
        />
      </div>

      {/* 协议勾选 */}
      {agreements.length > 0 && (
        <div className="space-y-3">
          <AgreementCheckbox
            agreements={agreements}
            checked={agreementsChecked}
            onCheckedChange={setAgreementsChecked}
            error={agreementError}
          />
        </div>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading}
        aria-busy={isLoading}
        size="lg"
      >
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {isLoading
          ? t("domestic.registering", "注册中...")
          : t("domestic.loginRegisterButton")}
      </Button>

      {/* 区域感知的注册阻断弹窗 */}
      <RegistrationBlockingModal {...modalProps} />
    </form>
  );
}
